import { ExpenseInfo, ExpenseConfiguration } from "@/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface ExpensesTableProps {
  expenses: ExpenseInfo[];
  expenseConfigurations: ExpenseConfiguration[];
  totalExpenses: number;
  showAllExpenses: boolean;
  tripId: string | undefined;
  maxExpenses?: number;
}

export const ExpensesTable = ({ 
  expenses, 
  expenseConfigurations, 
  totalExpenses, 
  showAllExpenses,
  tripId,
  maxExpenses = 10
}: ExpensesTableProps): React.ReactNode => {
  const displayExpenses = showAllExpenses ? expenses : expenses.slice(0, maxExpenses);

  const getCategoryBadgeColor = (category: string) => {
    // Simple hash function to get consistent colors for categories
    const hash = category.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colors = ['default', 'secondary', 'outline'];
    return colors[hash % colors.length];
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Expenses</CardTitle>
        <CardDescription>
          {showAllExpenses ? 'All expenses for this trip' : `Recent ${maxExpenses} expenses`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayExpenses.map((expense, index) => (
              <TableRow key={index}>
                <TableCell>{new Date(expense.date).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Badge variant={getCategoryBadgeColor(expense.category) as any}>
                    {expense.category}
                  </Badge>
                </TableCell>
                <TableCell>{expense.description}</TableCell>
                <TableCell>
                  <Badge variant={expense.isPlanned ? 'outline' : 'default'}>
                    {expense.isPlanned ? 'Planned' : 'Actual'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-medium">
                  {expense.currency} {expense.amount.toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
            {displayExpenses.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No expenses recorded yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

